<script setup>
import { ref, computed } from "vue"
import { useIsMounted } from "@/assets/js/mixins"
import { useAuthStore } from "@/stores/auth.js"
import gql from "graphql-tag"
import { useQuery } from "@vue/apollo-composable"
import { DateTime } from "luxon"
import { useToggleThemeStore } from "@/stores/toggleTheme"
import PUBLISHER_CAMPAIGNS_LITE_QUERY from "@/graphql/PublisherCampaign/PublisherCampaignsQueryLite.gql"
import PUBLISHER_CAMPAIGNS_QUERY from "@/graphql/PublisherCampaign/PublisherCampaignsQuery.gql"
import DASHBOARD_REPORT_QUERY from "@/graphql/Stats/DashboardStats.gql"
import TOP_CAMPAIGNS_FOR_USER from "@/graphql/Stats/AllInTopCampaignsByUser.gql"
import TOTAL_CAMPAIGNS_SUBSCRIBED from "@/graphql/Stats/AllInTotalCampaignsSubscribed.gql"

import FeaturedCampaignCard from "./components/FeaturedCampaignCard.vue"
import UserTopCampaignCard from "./components/UserTopCampaignCard.vue"
import UserTopCampaignLoading from "./components/UserTopCampaignLoading.vue"
import StatCard from "./components/StatCard.vue"
import Banner from "@/components/common/Banner.vue"
import { formatCurrency } from "@/utils/formatCurrency"

const authStore = useAuthStore()
const themeStore = useToggleThemeStore()
const darkTheme = computed(() => themeStore.activeTheme == "night")


// featuredCampaigns
const featuredCampaignVariables = computed(() => ({
  publisherId: authStore?.authUser?.id,
  filters: {
    featured: true,
  },
  first: 20,
  orderBy: [
    {
      column: "featured_at",
      order: "DESC",
    },
    {
      column: "id",
      order: "DESC",
    },
  ],
}))

const { result: resultFeaturedCampaigns, loading: featuredCampaignsIsLoading } =
  useQuery(PUBLISHER_CAMPAIGNS_LITE_QUERY, featuredCampaignVariables)

const {
  result: fullResultFeaturedCampaigns,
  loading: fullFeaturedCampaignsIsLoading,
} = useQuery(PUBLISHER_CAMPAIGNS_QUERY, featuredCampaignVariables)

const featuredCampaigns = computed(
  () =>
    fullResultFeaturedCampaigns.value?.publisherCampaigns?.data ||
    resultFeaturedCampaigns.value?.publisherCampaigns?.data ||
    [],
)

//total subscribed campaigns
const totalSubscribedCampaignsVariables = computed(() => ({
  id: authStore.authUser.id,
}))

const { result: totalSubscribedCampaigns } = useQuery(
  TOTAL_CAMPAIGNS_SUBSCRIBED,
  totalSubscribedCampaignsVariables,
  { enabled: computed(() => !!authStore.authUser?.id) },
)
const totalSubscribed = computed(
  () =>
    totalSubscribedCampaigns.value?.totalCampaignsSubscribed
      ?.totalSubscribed || 0,
)

//filter dateRange
const currentDateRange = ref("thisMonth")

const updateDateRange = range => {
  const today = DateTime.local()
  switch (range) {
    case "today":
      currentDateRange.value = "today"
      dateRangeFilter.value = [
        today.toFormat("yyyy-LL-dd"),
        today.toFormat("yyyy-LL-dd"),
      ]
      break
    case "yesterday":
      currentDateRange.value = "yesterday"
      const yesterday = today.minus({ days: 1 })
      dateRangeFilter.value = [
        yesterday.toFormat("yyyy-LL-dd"),
        yesterday.toFormat("yyyy-LL-dd"),
      ]
      break
    case "thisMonth":
      currentDateRange.value = "thisMonth"
      dateRangeFilter.value = [
        today.startOf("month").toFormat("yyyy-LL-dd"),
        today.toFormat("yyyy-LL-dd"),
      ]
      break
    case "lastMonth":
      currentDateRange.value = "lastMonth"
      const lastMonthStart = today.minus({ months: 1 }).startOf("month")
      const lastMonthEnd = today.minus({ months: 1 }).endOf("month")
      dateRangeFilter.value = [
        lastMonthStart.toFormat("yyyy-LL-dd"),
        lastMonthEnd.toFormat("yyyy-LL-dd"),
      ]
      break
    default:
      currentDateRange.value = "thisMonth"
      dateRangeFilter.value = [
        today.startOf("month").toFormat("yyyy-LL-dd"),
        today.toFormat("yyyy-LL-dd"),
      ]
  }
  
  // Close the dropdown after selection (DaisyUI recommended approach)
  document.activeElement.blur()
}

const dateRangeFilter = ref([
  DateTime.local().startOf("month").toFormat("yyyy-LL-dd"),
  DateTime.local().toFormat("yyyy-LL-dd"),
])

const dateRangeLabels = computed(() => ({
  today: 'Today',
  yesterday: 'Yesterday',
  thisMonth: 'This Month',
  lastMonth: 'Last Month'
}))

const shouldHideTableOnMobile = computed(() => {
  return currentDateRange.value === 'today' || currentDateRange.value === 'yesterday'
})

// Main table & stats query
const userReportVariables = computed(() => ({
  filters: { dateRange: dateRangeFilter.value },
  pagination: 1,
  sortOrder: [{ field: "day", sortField: "day", direction: "desc" }],
  export: false,
  fields: ["day", "visits", "publisherConversions", "ecpm", "payout"],
}))
const {
  result: userReportResult,
  loading: isLoadingUserReport,
  error,
} = useQuery(DASHBOARD_REPORT_QUERY, userReportVariables)
const userReport = computed(() => userReportResult.value?.dailyGraphData || [])

//MyCampaigns query
const myCampaignReportVariables = computed(() => {
  if (!authStore.authUser) {
    return null
  }
  return {
    input: {
      dateRange: dateRangeFilter.value,
      user: {
        userId: authStore.authUser.id,
        label: authStore.authUser.label,
      },
    },
  }
})

const { result: myCampaignsReportResult, loading: myCampaignsIsLoading } =
  useQuery(TOP_CAMPAIGNS_FOR_USER, myCampaignReportVariables, {
    enabled: computed(() => !!myCampaignReportVariables.value),
  })

const myCampaigns = computed(
  () => myCampaignsReportResult?.value?.topCampaignsByUser || [],
)
const myCampaignsClipped = computed(() => myCampaigns.value)

// Current revenue for quarantine progress (placeholder - replace with actual data)
const currentRevenue = computed(() => {
  // This should come from your actual revenue data
  // For now, using a placeholder based on user report data
  return userReport.value?.totals?.payout || 0
})
</script>

<template>
  <section class="flex flex-col md:flex-row gap-3 xl:gap-6">
    <!-- Featured Campaigns Section -->
    <div class="flex-1 w-full overflow-hidden">
      <SectionHeader>
        <template #left>
          <h2>Featured Campaigns</h2>
        </template>
      </SectionHeader>
      <div
        v-if="featuredCampaignsIsLoading"
        class="h-[20.2rem] items-center flex w-full justify-center"
      >
        <span class="text-base-content/25">
          <v-icon
            class="ov-spin"
            name="ri-loader-5-line"
            height="1em"
            width="1em"
            scale="1.5"
          />
        </span>
      </div>
      <Carousel
        v-if="featuredCampaigns && featuredCampaigns?.length > 0"
        :slideCount="featuredCampaigns.length"
      >
        <template
          v-for="(campaign, index) in featuredCampaigns"
          :key="index"
          v-slot:[`slide-${index+1}`]
        >
          <FeaturedCampaignCard :campaign="campaign" class="mr-5" />
        </template>
      </Carousel>
    </div>

    <!-- Account Status Section (only shown when quarantined) -->
    <div v-if="authStore.authUser?.quarantine" class="w-full md:w-64 xl:w-64 2xl:w-80 flex-shrink-0 flex flex-col pb-6">
      <SectionHeader>
        <template #left>
          <h2>Account Status</h2>
        </template>
      </SectionHeader>
      
      <!-- Quarantine Status Box -->
      <div class="panel p-3 flex-grow h-full">
        <!-- Quarantine Status -->
        <div class="h-full flex flex-col justify-between">
          <div class="flex flex-col gap-2">
          <!-- Title with Icon -->
          <div class="flex items-center gap-2 mb-4">
            <v-icon name="md-warning-round" class="text-warning flex-shrink-0" scale="1.5" />
            <h4 class="font-semibold text-base-content">
              Quarantine Level {{ authStore.authUser.quarantine?.level || 1 }}
            </h4>
          </div>
          
          <!-- Progress Bars (Full Width) -->
           <div>
          <div class="space-y-3 mb-4">
            <!-- Revenue Objective Progress -->
            <div v-if="authStore.authUser.quarantine?.objective?.value">
              <div class="flex justify-between text-xs font-medium mb-1">
                <span class="text-base-content/80">Revenue Goal</span>
                <span class="text-base-content/80">{{ formatCurrency(authStore.authUser.quarantine.objective.value) }}</span>
              </div>
              <div class="w-full bg-base-100/75 rounded-full h-3">
                <div 
                  class="bg-info h-3 rounded-full transition-all duration-500"
                  :style="{ 
                    minWidth: '0.8rem',
                      width: `${Math.min((currentRevenue / authStore.authUser.quarantine.objective.value) * 100, 100)}%`
                     }"
                ></div>
              </div>
            </div>
          </div>
            
            <!-- Revenue Cap Progress -->
            <div v-if="authStore.authUser.quarantine?.capLimit">
              <div class="flex justify-between text-xs font-medium mb-1">
                <span class="text-base-content/80">Revenue Cap</span>
                <span class="text-base-content/80">{{ formatCurrency(authStore.authUser.quarantine.capLimit) }}</span>
              </div>
              <div class="w-full bg-base-100/75 rounded-full h-3">
                <div 
                  class="bg-warning h-3 rounded-full transition-all duration-500"
                  :style="{ 
                    minWidth: '0.8rem',
                    width: `${Math.min((currentRevenue / authStore.authUser.quarantine.capLimit) * 100, 100)}%`
                   }"
                ></div>
              </div>
            </div>
          </div>
        </div>
          <!-- Explanation Banner -->
          <Banner 
            bannerClass="text-xs text-left rounded-b-md rounded-t-md mt-4" 
            variant="info"
            :simple="true"
          >
            Hit your monthly revenue goal to increase next month's cap.
          </Banner>

        </div>
      </div>
    </div>
  </section>

  <div class="text-white mt-2">
    <!-- <div class="divider"></div> -->
  </div>

  <section class="grid grid-cols-1 gap-0">
    <SectionHeader>
      <template #left>
        <h2>Statistics</h2>
      </template>
      <template #right>
        <!-- Desktop buttons (md and above) -->
        <div class="hidden md:grid grid-cols-4 gap-1">
          <button
            class="btn btn-ghost btn-sm"
            :class="{ activeDateButton: currentDateRange == 'today' }"
            @click="updateDateRange('today')"
          >
            Today
          </button>
          <button
            class="btn btn-ghost btn-sm"
            :class="{
              activeDateButton: currentDateRange == 'yesterday',
            }"
            @click="updateDateRange('yesterday')"
          >
            Yesterday
          </button>
          <button
            class="btn btn-ghost btn-sm"
            :class="{ activeDateButton: currentDateRange == 'thisMonth' }"
            @click="updateDateRange('thisMonth')"
          >
            This Month
          </button>
          <button
            class="btn btn-ghost btn-sm"
            :class="{ activeDateButton: currentDateRange == 'lastMonth' }"
            @click="updateDateRange('lastMonth')"
          >
            Last Month
          </button>
        </div>
        
        <!-- Mobile dropdown (below md) -->
        <div class="dropdown dropdown-end md:hidden">
          <div 
            tabindex="0" 
            role="button" 
            class="btn btn-ghost btn-sm gap-2"
          >
            <span>{{ dateRangeLabels[currentDateRange] }}</span>
            <svg class="w-4 h-4 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </div>
          <ul 
            tabindex="0" 
            class="dropdown-content menu bg-base-100 rounded-box z-[100] w-52 p-2 shadow-lg border border-base-300"
          >
            <li>
              <a 
                @click="updateDateRange('today')"
                :class="{ 'active !text-primary dark:text-primary': currentDateRange == 'today' }"
              >
                Today
              </a>
            </li>
            <li>
              <a 
                @click="updateDateRange('yesterday')"
                :class="{ 'active !text-primary dark:text-primary': currentDateRange == 'yesterday' }"
              >
                Yesterday
              </a>
            </li>
            <li>
              <a 
                @click="updateDateRange('thisMonth')"
                :class="{ 'active !text-primary dark:text-primary': currentDateRange == 'thisMonth' }"
              >
                This Month
              </a>
            </li>
            <li>
              <a 
                @click="updateDateRange('lastMonth')"
                :class="{ 'active !text-primary dark:text-primary': currentDateRange == 'lastMonth' }"
              >
                Last Month
              </a>
            </li>
          </ul>
        </div>
      </template>
    </SectionHeader>

    <!-- Mobile: 2x2 ultra-compact StatCards -->
    <div class="lg:hidden grid grid-cols-2 gap-3">
      <div class="mobile-stat-card">
        <StatCard
          title="Impressions"
          :value="userReport.totals?.visits ?? 0"
          :loading="isLoadingUserReport"
          iconName="hi-eye"
          cardClass="stats-card-1"
          titleClass="text-sm"
          valueClass="text-md font-semibold"
          iconClass="w-6 h-6"
        />
      </div>
      <div class="mobile-stat-card">
        <StatCard
          title="Conversions"
          :value="userReport.totals?.publisherConversions ?? 0"
          :loading="isLoadingUserReport"
          iconName="hi-cursor-click"
          cardClass="stats-card-1"
          titleClass="text-sm"
          valueClass="text-md font-semibold"
          iconClass="w-6 h-6"
        />
      </div>
      <div class="mobile-stat-card">
        <StatCard
          title="ECPM"
          :value="formatCurrency(userReport?.totals?.ecpm)"
          :loading="isLoadingUserReport"
          iconName="ri-coins-line"
          cardClass="stats-card-2"
          titleClass="text-sm"
          valueClass="text-md font-semibold"
          iconClass="w-6 h-6"
        />
      </div>
      <div class="mobile-stat-card">
        <StatCard
          title="Revenue"
          :value="formatCurrency(userReport?.totals?.payout)"
          :loading="isLoadingUserReport"
          iconName="md-attachmoney-round"
          cardClass="stats-card-3"
          titleClass="text-sm"
          valueClass="text-md font-semibold"
          iconClass="w-6 h-6"
        />
      </div>
    </div>

    <!-- Desktop: Original stat cards -->
    <div class="hidden lg:grid grid-cols-1 lg:grid-cols-4 gap-3 xl:gap-6">
      <StatCard
        title="Impressions"
        :value="userReport.totals?.visits ?? 0"
        :loading="isLoadingUserReport"
        iconName="hi-eye"
        cardClass="stats-card-1"
      />
      <StatCard
        title="Conversions"
        :value="userReport.totals?.publisherConversions ?? 0"
        :loading="isLoadingUserReport"
        iconName="hi-cursor-click"
        cardClass="stats-card-1"
      />
      <StatCard
        title="ECPM"
        :value="formatCurrency(userReport?.totals?.ecpm)"
        :loading="isLoadingUserReport"
        iconName="ri-coins-line"
        cardClass="stats-card-2"
      />
      <StatCard
        title="Revenue"
        :value="formatCurrency(userReport?.totals?.payout)"
        :loading="isLoadingUserReport"
        iconName="md-attachmoney-round"
        cardClass="stats-card-3"
      />
    </div>
  </section>

  <section class="mt-6 grid grid-cols-12  gap-3 xl:gap-6 pb-6">
    <div class="col-span-12 md:col-span-6 lg:col-span-8 sm:min-h-[560px]" :class="{ 'hidden md:block': shouldHideTableOnMobile }">
      <XTable
        :loading="isLoadingUserReport"
        :data="userReport.data || []"
        emptyText="No statistics for this period"
      >
        <template v-slot:header-row>
          <th class="table-column-fixed-width">Day</th>
          <th class="table-column-fixed-width">
            <span class="hidden sm:block">Impressions</span>
            <span class="block sm:hidden overflow-hidden" v-tooltip="{ content: 'Impressions', triggers: ['hover', 'click'] }">Impressions</span>
          </th>
          <th class="table-column-fixed-width">
            <span class="hidden sm:block">Conversions</span>
            <span class="block sm:hidden  overflow-hidden" v-tooltip="{ content: 'Conversions', triggers: ['hover', 'click'] }">Conversions</span>
          </th>
          <th class="table-column-fixed-width">
            <span class="hidden sm:inline">ECPM</span>
            <span class="inline sm:hidden  overflow-hidden " v-tooltip="{ content: 'ECPM', triggers: ['hover', 'click'] }">ECPM</span>
          </th>
          <th class="table-column-fixed-width">
            <span class="hidden sm:inline">Revenue</span>
            <span class="inline sm:hidden overflow-hidden" v-tooltip="{ content: 'Revenue', triggers: ['hover', 'click'] }">Revenue</span>
          </th>
        </template>
        <template v-slot:row="props">
          <td class="table-column-fixed-width">
            {{ props.row.event_date }}
          </td>
          <td class="table-column-fixed-width">
            {{ props.row.visits.toLocaleString() }}
          </td>
          <td class="table-column-fixed-width">
            {{ props.row.publisherConversions.toLocaleString() }}
          </td>
          <td class="table-column-fixed-width">
            {{ formatCurrency(props.row.ecpm) }}
          </td>
          <td class="table-column-fixed-width">
            {{ formatCurrency(props.row.payout) }}
          </td>
        </template>
      </XTable>
    </div>

    <div class="col-span-12 md:col-span-6 lg:col-span-4">
      <XTable
        v-if="!myCampaignsIsLoading"
        :loading="myCampaignsIsLoading"
        :data="myCampaignsClipped || []"
        emptyText="You are not subscribed to any campaigns"
        :fillHeight="false"
      >
        <template v-slot:header-row>
          <th>My Campaigns</th>
        </template>
        <template v-slot:row="props">
          <td class="!py-3">
            <UserTopCampaignCard :campaign="props.row" />
          </td>
        </template>
        <template v-slot:afterBodyRow v-if="totalSubscribed > 5">
          <td>
            <p class="text-xs">
              <router-link
                :to="{
                  name: 'Campaigns',
                  query: { publisherStatus: 'Subscribed' },
                }"
                class="link link-primary"
                >View All</router-link
              >
            </p>
          </td></template
        >
      </XTable>
      <div v-if="myCampaignsIsLoading">
        <UserTopCampaignLoading />
      </div>
    </div>
  </section>
</template>

<style scoped>


.table-column-fixed-width {
  white-space: nowrap;
  overflow: hidden;
}
@media (min-width: 640px) {
  .table-column-fixed-width {
    width: 120px;
    /* max-width: 120px;
    min-width: 120px; */
    white-space: nowrap;
    overflow: hidden;
  }
}



button:hover {
  cursor: pointer;
}
.activeDateButton {
  @apply bg-zinc-300 text-primary dark:bg-primary dark:text-accent-content border-transparent font-medium;
}
.activeDateButton:hover {
  @apply bg-zinc-400  text-white dark:bg-accent  dark:text-accent-content border-transparent font-medium;
}

.mobile-stat-card :deep(.stat-card-inner) {
  @apply py-3 px-4 gap-4;
}

.mobile-stat-card :deep(.stat-card-inner .flex.flex-col) {
  @apply gap-1;
}
</style>
